import { computed, defineComponent, inject, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import html_iway from "./template.iway.html?raw";
import { orderServiceKey } from "../../../infrastructure/gfo_api/order_service";
import type { ConfigDto, LandlinePortingConfigPatchRequest } from "../../../infrastructure/gfo_api/order_api";
import { ApiValidationError } from "../../../infrastructure/gfo_api/api_validation_error";
import ErrorMessage from "../../Shared/ErrorMessage";
import SwissLandlineNumberEntry from "../../Shared/SwissLandlineNumberEntry";
import VueDatePicker from "../../Shared/LazyDatePicker";
import { de } from "date-fns/locale";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}

// Min. 7 Arbeitstage nach Einreichung des Portierungsformulars
const MIN_BUSINESS_DAYS = 7;
// Die Anfrage darf höchstens 365 Tage vor Vertragsablauf gestellt werden
const MAX_CONTRACT_EXPIRY_DAYS = 365;

const WISHDATE_TYPE_WISHDATE = "wishdate";
const WISHDATE_TYPE_CONTRACT_EXPIRY = "contract_expiry";

interface Props {
  config: ConfigDto;
}

export default defineComponent({
  template: html,
  components: { ErrorMessage, SwissLandlineNumberEntry, VueDatePicker },
  props: {
    config: {
      type: Object as () => ConfigDto,
      required: true,
    },
  },
  setup(props: Props) {
    // Injections
    const { t } = useI18n();
    const orderService = inject(orderServiceKey);

    // vars und computed
    const generalError = ref("");
    const landlineNumber = ref<string | null>(null);
    const landlineNumberError = ref("");
    const wishdateType = ref<string>(WISHDATE_TYPE_WISHDATE);
    const wishdate = ref<Date | null>(null);
    const wishdateError = ref("");

    const wishdateTypeChoices = computed(() => [
      {
        key: WISHDATE_TYPE_WISHDATE,
        text: t("ConfigsPhone_LandlinePorting_WishdateType_Wishdate"),
        info: t("ConfigsPhone_LandlinePorting_WishdateType_Wishdate_Info"),
      },
      {
        key: WISHDATE_TYPE_CONTRACT_EXPIRY,
        text: t("ConfigsPhone_LandlinePorting_WishdateType_ContractExpiry"),
        info: t("ConfigsPhone_LandlinePorting_WishdateType_ContractExpiry_Info"),
      },
    ]);

    // methods
    const addBusinessDays = (date: Date, days: number): Date => {
      const result = new Date(date);
      let added = 0;
      while (added < days) {
        result.setDate(result.getDate() + 1);
        const day = result.getDay();
        if (0 != day && 6 != day) {
          added++;
        }
      }
      return result;
    };

    const disabledDates = (dt: Date) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (WISHDATE_TYPE_CONTRACT_EXPIRY == wishdateType.value) {
        const latest = new Date(today);
        latest.setDate(latest.getDate() + MAX_CONTRACT_EXPIRY_DAYS);
        return dt < today || dt > latest;
      }

      const earliest = addBusinessDays(today, MIN_BUSINESS_DAYS);
      return dt < earliest;
    };

    // laufender Autosave-Request; validate() muss darauf warten, bevor es den
    // (sonst optimistisch geleerten) Fehlerstatus auswertet
    let pendingSave: Promise<void> | null = null;

    const save = async () => {
      const run = async () => {
        landlineNumberError.value = "";
        wishdateError.value = "";
        generalError.value = "";
        if (orderService == null) {
          return;
        }
        const wishdateStr = ((): string | null => {
          if (null == wishdate.value) {
            return null;
          }
          const yyyy = wishdate.value.getFullYear();
          const mm = String(wishdate.value.getMonth() + 1).padStart(2, "0");
          const dd = String(wishdate.value.getDate()).padStart(2, "0");
          return `${yyyy}-${mm}-${dd}`;
        })();
        const request: LandlinePortingConfigPatchRequest = {
          landline_number: landlineNumber.value,
          wishdate: wishdateStr,
          wishdate_type: wishdateType.value,
        };
        try {
          await orderService.patchConfig(props.config.uuid, props.config.type, request);
        } catch (err) {
          if (err instanceof ApiValidationError) {
            landlineNumberError.value = err.errors["landline_number"]?.[0] ?? "";
            wishdateError.value = err.errors["wishdate"]?.[0] ?? "";
          } else {
            generalError.value = `${err}`;
          }
        }
      };
      pendingSave = run();
      await pendingSave;
    };

    const onWishdateTypeChange = async () => {
      await save();
    };

    const onUpdateWishdate = async (evt: Date | null) => {
      wishdate.value = evt;
      await save();
    };

    const isValid = computed(() => {
      if (!landlineNumber.value || !wishdate.value) {
        return false;
      }
      return !landlineNumberError.value && !wishdateError.value;
    });

    const validate = async () => {
      if (pendingSave) {
        await pendingSave;
      }
      if (!landlineNumber.value) {
        landlineNumberError.value = t("Validation_Field_Required");
      }
      if (!wishdate.value) {
        wishdateError.value = t("Validation_Field_Required");
      }
      return isValid.value;
    };

    onMounted(() => {
      landlineNumber.value = props.config.data?.landline_number ?? null;
      wishdateType.value = props.config.data?.wishdate_type ?? WISHDATE_TYPE_WISHDATE;
      wishdate.value =
        null == props.config.data?.wishdate ? null : new Date(props.config.data.wishdate);
    });

    return {
      t,
      de,
      generalError,
      landlineNumber,
      landlineNumberError,
      wishdateType,
      wishdateTypeChoices,
      wishdate,
      wishdateError,
      disabledDates,
      onWishdateTypeChange,
      onUpdateWishdate,
      save,
      isValid,
      validate,
    };
  },
} as any);
